import React, { useState, useEffect, useRef, useCallback } from 'react';
import config, { authFetch, clearAuthToken } from './config';
import './App.css';
import Auth from './components/Auth';
import ReviewsList from './components/ReviewsList';
import PDFsList from './components/PDFsList';
import PDFViewer from './components/PDFViewer';
import CommentPanel from './components/CommentPanel';
import SharePanel from './components/SharePanel';

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Navigation state: 'auth' | 'reviews' | 'pdfs' | 'editor'
  const [currentView, setCurrentView] = useState('auth');
  const [selectedReview, setSelectedReview] = useState(null);
  const [isSharedReview, setIsSharedReview] = useState(false);
  
  // PDF editor state
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  const [shareId, setShareId] = useState(null);
  const [comments, setComments] = useState([]);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [commentPanelVisible, setCommentPanelVisible] = useState(true);
  const [pdfReady, setPdfReady] = useState(false);
  const commentDeleteRef = useRef(null);
  const commentUpdateRef = useRef(null);
  const commentReplyRef = useRef(null);
  const commentMarkReadRef = useRef(null);
  const exportRef = useRef(null);
  
  // User name for PDF editor (use authenticated user's name)
  const userName = user?.name || `User ${Math.floor(Math.random() * 10000)}`;

  const loadSharedPDF = useCallback(async (shareIdParam) => {
    try {
      const response = await fetch(`${config.API_URL}/api/share/${shareIdParam}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 401) {
          setCurrentView('auth');
          return;
        }
        throw new Error('Failed to load shared PDF');
      }
      
      const data = await response.json();
      const baseUrl = config.API_URL.endsWith('/') ? config.API_URL.slice(0, -1) : config.API_URL;
      const pdfPath = data.pdfUrl.startsWith('/') ? data.pdfUrl : `/${data.pdfUrl}`;
      const fullUrl = `${baseUrl}${pdfPath}`;
      
      setPdfUrl(fullUrl);
      setPdfId(data.pdfId);
      setShareId(data.shareId);
      setCurrentView('editor');
      setPdfReady(false);
    } catch (error) {
      console.error('Error loading shared PDF:', error);
      alert('Failed to load shared PDF. Please make sure you are logged in.');
    }
  }, []);

  const loadSharedReview = useCallback(async (shareIdParam) => {
    try {
      const response = await fetch(`${config.API_URL}/api/share/review/${shareIdParam}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 401) {
          setCurrentView('auth');
          return;
        }
        throw new Error('Failed to load shared review');
      }
      const data = await response.json();
      setSelectedReview(data.review);
      setIsSharedReview(true);
      // When opening a shared review, always land on the PDFs list for that review
      // so the second user sees the full project context instead of a specific PDF.
      setCurrentView('pdfs');
    } catch (error) {
      console.error('Error loading shared review:', error);
      alert('Failed to load shared review. Please make sure you are logged in.');
    }
  }, []);

  // Check authentication on mount and handle share links
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shareParam = urlParams.get('share');
    const shareReviewParam = urlParams.get('shareReview');

    const checkAuth = async () => {
      try {
        const response = await authFetch(`${config.API_URL}/api/auth/me`);
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setCurrentView('reviews');
          if (shareParam) {
            loadSharedPDF(shareParam);
          } else if (shareReviewParam) {
            loadSharedReview(shareReviewParam);
          }
        } else {
          setCurrentView('auth');
        }
      } catch (err) {
        setCurrentView('auth');
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, [loadSharedPDF, loadSharedReview]);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    const params = new URLSearchParams(window.location.search);
    const shareParam = params.get('share');
    const shareReviewParam = params.get('shareReview');
    if (shareParam) {
      loadSharedPDF(shareParam);
    } else if (shareReviewParam) {
      loadSharedReview(shareReviewParam);
    } else {
      setCurrentView('reviews');
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${config.API_URL}/api/auth/logout`, {
        method: 'POST',
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    clearAuthToken();
    setUser(null);
    setCurrentView('auth');
    setSelectedReview(null);
    setPdfUrl(null);
    setPdfId(null);
    setShareId(null);
  };

  const handleSelectReview = (review) => {
    setSelectedReview(review);
    setIsSharedReview(false);
    setCurrentView('pdfs');
  };

  const handleBackToReviews = () => {
    setSelectedReview(null);
    setIsSharedReview(false);
    setCurrentView('reviews');
  };

  const handleSelectPDF = async (pdf) => {
    try {
      // Load PDF details
      const response = await authFetch(`${config.API_URL}/api/pdf/${pdf.id}`);
      if (!response.ok) throw new Error('Failed to load PDF');
      
      const data = await response.json();
      const baseUrl = config.API_URL.endsWith('/') ? config.API_URL.slice(0, -1) : config.API_URL;
      const pdfPath = data.pdfUrl.startsWith('/') ? data.pdfUrl : `/${data.pdfUrl}`;
      const fullUrl = `${baseUrl}${pdfPath}`;
      
      setPdfUrl(fullUrl);
      setPdfId(pdf.id);
      setShareId(pdf.shareId);
      setCurrentView('editor');
      setPdfReady(false);
    } catch (err) {
      console.error('Error loading PDF:', err);
      alert('Failed to load PDF');
    }
  };

  const handleBackFromEditor = () => {
    if (selectedReview) {
      setCurrentView('pdfs');
    } else {
      setCurrentView('reviews');
    }
    setPdfUrl(null);
    setPdfId(null);
    setShareId(null);
    setComments([]);
  };

  if (checkingAuth) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="transition-loading-card">
            <div className="transition-loading-spinner" />
            <div className="transition-loading-text">Checking your session…</div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'auth') {
    return (
      <div className="App">
        <Auth onAuthSuccess={handleAuthSuccess} />
      </div>
    );
  }

  if (currentView === 'reviews') {
    return (
      <div className="App">
        <ReviewsList
          user={user}
          onSelectReview={handleSelectReview}
          onCreateReview={() => {}} // Handled internally
          onLogout={handleLogout}
        />
      </div>
    );
  }

  if (currentView === 'pdfs') {
    return (
      <div className="App">
        <PDFsList
          review={selectedReview}
          user={user}
          onSelectPDF={handleSelectPDF}
          onBackToReviews={handleBackToReviews}
          isSharedReview={isSharedReview}
        />
      </div>
    );
  }

  // Editor view
  return (
    <div className="App">
      {/* Navigation bar */}
      <div className="editor-nav">
        <button onClick={handleBackFromEditor} className="nav-back-button">
          ← {selectedReview ? 'Back to PDFs' : 'Back to Reviews'}
        </button>
        {shareId && <SharePanel shareId={shareId} />}
      </div>
      
      <div className="editor-container">
        <div className={`pdf-section ${pdfReady && !commentPanelVisible ? 'pdf-section-full' : ''} ${!pdfReady ? 'pdf-section-loading' : ''}`}>
          <PDFViewer 
            pdfUrl={pdfUrl} 
            pdfId={pdfId}
            shareId={shareId}
            userName={userName}
            comments={comments}
            onCommentsChange={setComments}
            onCommentDelete={commentDeleteRef}
            onCommentUpdate={commentUpdateRef}
            onCommentReply={commentReplyRef}
            onCommentMarkRead={commentMarkReadRef}
            exportRef={exportRef}
            sharePanel={null} // Handled in nav bar
            selectedCommentId={selectedCommentId}
            onSelectComment={(comment) => {
              setSelectedCommentId(comment?.id ?? null);
              if (comment?.page && exportRef.current?.setPageNumber) {
                exportRef.current.setPageNumber(comment.page);
              }
            }}
            onDocumentReady={() => setPdfReady(true)}
          />
        </div>
        {pdfReady && (
          <>
            <button
              type="button"
              className={`comments-toggle-icon ${commentPanelVisible ? '' : 'comments-toggle-icon--show-only'}`}
              onClick={() => setCommentPanelVisible(!commentPanelVisible)}
              title={commentPanelVisible ? 'Hide comments' : 'Show comments'}
              aria-label={commentPanelVisible ? 'Hide comments panel' : 'Show comments panel'}
            >
              {commentPanelVisible ? '▶' : '◀'}
            </button>
            <div className={`comments-section ${commentPanelVisible ? '' : 'comments-section--hidden'}`}>
              <CommentPanel 
                pdfId={pdfId}
                comments={comments}
                onCommentsChange={setComments}
                onDeleteComment={(id) => commentDeleteRef.current?.(id)}
                onUpdateComment={(id, text) => commentUpdateRef.current?.(id, text)}
                onReplyComment={(commentId, reply) => commentReplyRef.current?.(commentId, reply)}
                onMarkCommentRead={(id, isRead) => commentMarkReadRef.current?.(id, isRead)}
                userName={userName}
                selectedCommentId={selectedCommentId}
                onSelectComment={(comment) => {
                  setSelectedCommentId(comment?.id ?? null);
                  if (comment?.page && exportRef.current?.setPageNumber) {
                    exportRef.current.setPageNumber(comment.page);
                  }
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default App;
